import { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Send, CheckCircle } from "lucide-react";

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    category: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // デモ用：実際の送信はせず、成功を表示
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div>
        <Header />
        <main className="pt-24 pb-16">
          <div className="container">
            <div className="mx-auto max-w-lg text-center">
              <div className="rounded-2xl bg-white p-8 shadow-sm border border-stone-200">
                <div className="mx-auto w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mb-6">
                  <CheckCircle className="w-8 h-8 text-teal-600" />
                </div>
                <h1 className="text-2xl font-bold text-stone-900">
                  送信完了しました
                </h1>
                <p className="mt-4 text-stone-600">
                  お問い合わせいただきありがとうございます。
                  <br />
                  内容を確認の上、担当者よりご連絡いたします。
                </p>
                <Button
                  className="mt-6"
                  onClick={() => {
                    setIsSubmitted(false);
                    setFormData({ name: "", email: "", category: "", message: "" });
                  }}
                >
                  新しいお問い合わせ
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <main className="pt-24 pb-16">
        <div className="container">
          <div className="mx-auto max-w-2xl">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-stone-900">お問い合わせ</h1>
              <p className="mt-2 text-stone-600">
                ご質問やご要望がございましたら、お気軽にお問い合わせください。
              </p>
            </div>

            <div className="rounded-2xl bg-white p-8 shadow-sm border border-stone-200">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">お名前 *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="山田 太郎"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">メールアドレス *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      placeholder="example@email.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">お問い合わせ種別 *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) =>
                      setFormData({ ...formData, category: value })
                    }
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="選択してください" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="store">店舗情報について</SelectItem>
                      <SelectItem value="request">掲載リクエスト</SelectItem>
                      <SelectItem value="correction">情報の修正依頼</SelectItem>
                      <SelectItem value="partnership">提携・広告について</SelectItem>
                      <SelectItem value="other">その他</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">お問い合わせ内容 *</Label>
                  <Textarea
                    id="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    placeholder="お問い合わせ内容をご記入ください"
                  />
                </div>

                <div className="bg-stone-50 rounded-lg p-4 text-sm text-stone-600">
                  <p>
                    ※ 通常2〜3営業日以内にご返信いたします。
                    <br />
                    ※ お急ぎの場合は、その旨をメッセージにご記載ください。
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting || !formData.category}
                >
                  {isSubmitting ? (
                    "送信中..."
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      送信する
                    </>
                  )}
                </Button>
              </form>
            </div>

            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              <div className="rounded-xl bg-white p-6 shadow-sm border border-stone-200">
                <h3 className="font-semibold text-stone-900">店舗掲載について</h3>
                <p className="mt-2 text-sm text-stone-600">
                  ベビーカーで入れるお店の情報をお寄せください。
                  実際に訪問された方からの情報をお待ちしています。
                </p>
              </div>
              <div className="rounded-xl bg-white p-6 shadow-sm border border-stone-200">
                <h3 className="font-semibold text-stone-900">情報の修正</h3>
                <p className="mt-2 text-sm text-stone-600">
                  掲載情報に誤りがある場合は、お問い合わせフォームより
                  ご連絡ください。確認の上、修正いたします。
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
